// ----------------------------------------------------------------------------
// Product Form Web Component: iamSellingPlanInput <iam-selling-plan-input>
// Extends: iamFormElement
// ----------------------------------------------------------------------------
// The Selling Plan Input is responsible for handling the selling plan selection UI.
// ----------------------------------------------------------------------------
// Event Listeners:
// - `productForm:variant:changed` → Dynamically refreshes selling plans on variant change
// ----------------------------------------------------------------------------
// Gotchas:
// - Unknown support with Preorders, multiple selling plan groups, or plans with >1 option input
// ----------------------------------------------------------------------------

// -------------------------------------
// Dependencies
// -------------------------------------

import iamotaEventHandler from 'scripts/helpers/iamota-event-handler';
import iamFormElement from 'scripts/classes/iamFormElement';
import { promoPriceOverrides } from '../promo-price-override/promo-price-overrides';
import registerCustomElement from 'scripts/helpers/register-custom-element';

// Styles
import './_ordergroove-offer.scss';

// Additional Features
import './ordergroove-offer--show-frequency.js'; // Show frequency in the OrderGroove offer

var SELLING_PLAN_ROW = '.selling-plan__option';
var OG_OPTION_CONTROL = 'og-optin-button, og-optout-button, input[type="radio"]';
var OG_UPSELL_MODAL = 'og-upsell-modal';
var OG_POINTER_CURSOR = 'pointer';
var OG_POINTER_CURSOR_SELECTORS = ['#ogOfferOptInLabel', '#ogOfferOptOutLabel', 'og-optin-button', 'og-optout-button'].join(',');
var OG_OPTION_HOVER_BORDER_COLOR = '#000';
var NESTED_INTERACTIVE = [OG_OPTION_CONTROL, 'og-tooltip', 'button', 'a', 'select', 'textarea'].join(',');
class iamOrdergrooveOffer extends iamFormElement {
  constructor() {
    super();
    this.handlePlanRowClick = this.handlePlanRowClick.bind(this);
    this.syncPlanRows = this.syncPlanRows.bind(this);
  }
  connectedCallback() {
    this.ogOffer = this.querySelector('og-offer');
    this.sellingPlanInput = this.querySelector('[name="selling_plan"]');
    this.bindSellingPlanOptionRows();

    // Broadcast Selling Plan Changes
    if (this.sellingPlanInput) {
      var triggerChange = () => {
        iamotaEventHandler.trigger('productForm:sellingPlanInput:changed', {
          sellingPlan: this.sellingPlanInput.value
        }, {
          context: this.iamForm
        });
      };

      // Trigger on change
      this.sellingPlanInput.addEventListener('change', triggerChange);

      // Optional: trigger on input if you want more immediate feedback
      this.sellingPlanInput.addEventListener('input', triggerChange);
    }
    super.connectedCallback();
  }
  disconnectedCallback() {
    var _this$planRowsObserve;
    this.removeEventListener('click', this.handlePlanRowClick);
    (_this$planRowsObserve = this.planRowsObserver) === null || _this$planRowsObserve === void 0 || _this$planRowsObserve.disconnect();
    this.planRowsBound = false;
    super.disconnectedCallback();
  }
  bindSellingPlanOptionRows() {
    if (this.planRowsBound) return;
    this.planRowsBound = true;
    this.addEventListener('click', this.handlePlanRowClick);
    this.syncPlanRows();
    this.planRowsObserver = new MutationObserver(this.syncPlanRows);
    this.planRowsObserver.observe(this, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['subscribed', 'checked']
    });
  }
  handlePlanRowClick(event) {
    var row = this.getClickedPlanRow(event);
    if (!row) return;
    this.selectPlanRow(row);
  }
  getClickedPlanRow(event) {
    var row = this.getPlanRowFromEvent(event);
    if (!row) return null;
    var path = typeof event.composedPath === 'function' ? event.composedPath() : [event.target];
    var nestedControl = path.find(node => node instanceof HTMLElement && node.matches(NESTED_INTERACTIVE));
    if (nestedControl && row.contains(nestedControl)) return null;
    return row;
  }
  getPlanRowFromEvent(event) {
    var path = typeof event.composedPath === 'function' ? event.composedPath() : [event.target];
    return path.find(node => node instanceof HTMLElement && node.matches(SELLING_PLAN_ROW) && node.hasAttribute('data-og-plan-row')) || null;
  }
  selectPlanRow(row) {
    var _row$querySelector;
    (_row$querySelector = row.querySelector(OG_OPTION_CONTROL)) === null || _row$querySelector === void 0 || _row$querySelector.click();
    window.requestAnimationFrame(this.syncPlanRows);
  }
  syncPlanRows() {
    this.getPlanGroups().forEach(group => {
      var rows = Array.from(group.querySelectorAll(SELLING_PLAN_ROW)).filter(row => row.querySelector(OG_OPTION_CONTROL));
      if (!rows.length) return;
      group.setAttribute('role', 'group');
      group.setAttribute('aria-label', group.getAttribute('aria-label') || 'Purchase options');
      rows.forEach(row => {
        row.dataset.ogPlanRow = 'true';
        row.removeAttribute('role');
        row.removeAttribute('aria-checked');
        row.removeAttribute('aria-label');
        row.removeAttribute('tabindex');
        this.syncPlanRowCursor(row);
      });
    });
    this.syncPlanRowHover();
  }
  syncPlanRowCursor(row) {
    row.style.cursor = OG_POINTER_CURSOR;
    row.querySelectorAll(OG_POINTER_CURSOR_SELECTORS).forEach(element => {
      element.style.cursor = OG_POINTER_CURSOR;
    });
    row.querySelectorAll(OG_OPTION_CONTROL).forEach(control => {
      var _control$shadowRoot;
      (_control$shadowRoot = control.shadowRoot) === null || _control$shadowRoot === void 0 || _control$shadowRoot.querySelectorAll('label, button').forEach(element => {
        element.style.cursor = OG_POINTER_CURSOR;
      });
    });
  }
  syncPlanRowHover() {
    this.getSellingPlanRowsInOpenRoots().forEach(row => {
      if (row.dataset.ogHoverBound) return;
      row.dataset.ogHoverBound = 'true';
      row.addEventListener('mouseenter', () => {
        row.style.borderColor = OG_OPTION_HOVER_BORDER_COLOR;
      });
      row.addEventListener('mouseleave', () => {
        row.style.removeProperty('border-color');
      });
    });
  }
  getSellingPlanRowsInOpenRoots() {
    var _root$querySelectorAl, _root$querySelectorAl2;
    var root = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : this;
    var rows = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : new Set();
    (_root$querySelectorAl = root.querySelectorAll) === null || _root$querySelectorAl === void 0 || _root$querySelectorAl.call(root, SELLING_PLAN_ROW).forEach(row => rows.add(row));
    (_root$querySelectorAl2 = root.querySelectorAll) === null || _root$querySelectorAl2 === void 0 || _root$querySelectorAl2.call(root, '*').forEach(element => {
      if (element.shadowRoot) {
        this.getSellingPlanRowsInOpenRoots(element.shadowRoot, rows);
      }
    });
    return Array.from(rows);
  }
  getPlanGroups() {
    var _this$ogOffer;
    return Array.from(((_this$ogOffer = this.ogOffer) === null || _this$ogOffer === void 0 ? void 0 : _this$ogOffer.querySelectorAll('.selling-plan')) || []).filter(group => !group.closest(OG_UPSELL_MODAL));
  }
  isPlanRowSelected(row) {
    var _row$getRootNode;
    var radio = row.querySelector('input[type="radio"]');
    if (radio !== null && radio !== void 0 && radio.checked) return true;
    var offer = row.closest('og-offer') || ((_row$getRootNode = row.getRootNode) === null || _row$getRootNode === void 0 || (_row$getRootNode = _row$getRootNode.call(row)) === null || _row$getRootNode === void 0 ? void 0 : _row$getRootNode.host);
    var isOneTime = row.classList.contains('selling-plan__option--one-time') || !!row.querySelector('og-optout-button');
    if (offer !== null && offer !== void 0 && offer.hasAttribute('subscribed')) {
      return !isOneTime;
    }
    return isOneTime;
  }
  getPlanRowLabel(row) {
    var _row$querySelector2, _row$querySelector3;
    var isOneTime = row.classList.contains('selling-plan__option--one-time') || !!row.querySelector('og-optout-button');
    var fallback = isOneTime ? 'One-time Purchase' : 'Subscribe and Save';
    var text = ((_row$querySelector2 = row.querySelector('.selling-plan__name')) === null || _row$querySelector2 === void 0 ? void 0 : _row$querySelector2.textContent) || ((_row$querySelector3 = row.querySelector(OG_OPTION_CONTROL)) === null || _row$querySelector3 === void 0 ? void 0 : _row$querySelector3.textContent) || fallback;
    var label = text.replace(/\s+/g, ' ').trim() || fallback;
    if (isOneTime && /one-time purchase/i.test(label)) {
      label = 'One-time Purchase';
    } else if (!isOneTime && /subscribe and save/i.test(label)) {
      label = 'Subscribe and Save';
    }
    return "".concat(label, " purchase option");
  }
  onFormInit() {
    iamotaEventHandler.subscribe('productForm:variant:changed', this.onVariantChange.bind(this), {
      context: this.iamForm,
      source: "ordergroove-offer.js"
    });

    // Initialize the OrderGroove offer component
    var variant = this.iamForm.getCurrentVariant();
    document.addEventListener('og-receive-offer', () => {
      // occurs when offers is ready to be rendered in the UI
      this.onVariantChange({
        variant
      });
      this.syncPlanRows();
    });
    this.onVariantChange({
      variant
    });
  }
  onVariantChange(_ref) {
    var {
      variant
    } = _ref;
    if (!variant) return;
    this.ogOffer.setAttribute('product', variant.id);

    /**
     * Subscription price & discount override
     */
    if (promoPriceOverrides.subscriptionPromo) {
      var subscriptionDiscount = promoPriceOverrides.subscriptionPromo.getDiscountPercent(variant.sku);

      // Subscription Discount percent label
      var discountLabels = this.ogOffer.querySelectorAll('og-incentive-text[from="DiscountPercent"][subscription]');
      discountLabels.forEach(label => {
        label.innerHTML = "".concat(subscriptionDiscount, "%");
      });

      // Subscription price elements
      var subAdjustedPriceElements = this.ogOffer.querySelectorAll('og-price[subscription]');
      var adjustedPrice = promoPriceOverrides.subscriptionPromo.calculateDiscountedPrice(variant.price, subscriptionDiscount);
      subAdjustedPriceElements.forEach(priceEl => {
        var adjustedPriceEl = document.createElement('span');
        adjustedPriceEl.slot = 'fallback';
        adjustedPriceEl.textContent = adjustedPrice;
        priceEl.replaceChildren(adjustedPriceEl);
      });
      var originalPriceElements = this.ogOffer.querySelectorAll('og-price:not([subscription])');
      var originalPrice = promoPriceOverrides.subscriptionPromo.formatCentsToDollars(variant.price);
      originalPriceElements.forEach(priceEl => {
        var originalPriceEl = document.createElement('span');
        originalPriceEl.slot = 'fallback';
        originalPriceEl.textContent = originalPrice;
        priceEl.replaceChildren(originalPriceEl);
      });
    }

    /**
     * One-Time purchase (non-subscription) price & discount override
     */
    if (promoPriceOverrides.oneTimePurchasePromo) {
      var oneTimePurchaseDiscountPercent = promoPriceOverrides.oneTimePurchasePromo.getDiscountPercent(variant.sku);

      // Only apply if the discount is greater than 0
      if (oneTimePurchaseDiscountPercent) {
        var oneTimePurchasePriceElements = this.ogOffer.querySelectorAll('og-price[regular].og-price--one-time');
        var oneTimePurchasePrice = promoPriceOverrides.oneTimePurchasePromo.calculateDiscountedPrice(variant.price, oneTimePurchaseDiscountPercent);
        oneTimePurchasePriceElements.forEach(priceEl => {
          var oneTimePurchasePriceEl = document.createElement('span');
          oneTimePurchasePriceEl.slot = 'fallback';
          oneTimePurchasePriceEl.textContent = oneTimePurchasePrice;
          priceEl.replaceChildren(oneTimePurchasePriceEl);
        });

        // Discount percent label
        var _discountLabels = this.ogOffer.querySelectorAll('og-incentive-text[from="DiscountPercent"][regular]');
        _discountLabels.forEach(label => {
          label.innerHTML = "".concat(oneTimePurchaseDiscountPercent, "%");
        });

        // show the original price and discount percent elements when one time is also discounted.
        this.ogOffer.querySelectorAll('.selling-plan__option--one-time .hide').forEach(el => el.classList.remove('hide'));
      }
    }
    this.syncPlanRows();
  }
}
registerCustomElement('iam-ordergroove-offer', iamOrdergrooveOffer);
export default iamOrdergrooveOffer;